/**
 * Hand-drawn cartoon face on the Earth.
 *
 * Canvas2D primitives (filled circles, arcs, thick strokes) draw the
 * face into a 512² canvas — eyes, brows, mouth, cheek blush. The canvas
 * is wrapped in a SpriteMaterial so the face always faces the camera no
 * matter how the Earth is rotated. This is intentionally NOT an emoji
 * glyph — every shape is drawn so the line weight, color, and proportions
 * are tuned for the cartoon-cel style of the rest of the scene.
 *
 * Emotion changes repaint the canvas; the GPU texture is refreshed via
 * `texture.needsUpdate = true` so we never need to recreate the sprite.
 */

import {
  CanvasTexture,
  LinearFilter,
  Sprite,
  SpriteMaterial,
} from "three";

export type EarthEmotion =
  | "calm"
  | "happy"
  | "nervous"
  | "worried"
  | "scared"
  | "celebrate";

const CANVAS_SIZE = 512;

interface FaceShape {
  /** Eye shape — round (open) vs squint (concerned). */
  eye: "round" | "squint" | "wide" | "closed-happy" | "wobble" | "starry";
  /** Eye scale multiplier — exaggerated for shocked/scared. */
  eyeScale: number;
  /** Mouth curve direction. Positive = smile, negative = frown. */
  mouthCurve: number;
  /** Mouth open height (0 = closed line, 1 = full open). */
  mouthOpen: number;
  /** Mouth width multiplier — wider for shock, narrower for nervous. */
  mouthScale: number;
  /** Eyebrow tilt (radians). 0 = neutral, + = angry/concerned, - = happy. */
  browTilt: number;
  /** Eyebrow vertical offset — raised brows for surprise. */
  browLift: number;
  /** Cheek blush opacity (0..1). */
  blush: number;
  /** Tint applied across the face (rgba) — red flush for scared. */
  tint?: string;
  /** Sweat drop on the temple — appears for nervous/scared. */
  sweat: boolean;
}

// Each emotion now has dramatically different shape parameters so the
// face change is unmistakable at a glance.
const SHAPES: Record<EarthEmotion, FaceShape> = {
  calm:      { eye: "round",        eyeScale: 1.00, mouthCurve:  0.20, mouthOpen: 0.10, mouthScale: 1.00, browTilt:  0.00, browLift:  0.00, blush: 0.40, sweat: false },
  happy:     { eye: "closed-happy", eyeScale: 1.10, mouthCurve:  0.70, mouthOpen: 0.40, mouthScale: 1.20, browTilt: -0.15, browLift: -0.02, blush: 0.65, sweat: false },
  nervous:   { eye: "wobble",       eyeScale: 0.90, mouthCurve: -0.15, mouthOpen: 0.20, mouthScale: 0.85, browTilt:  0.22, browLift:  0.00, blush: 0.30, sweat: true  },
  worried:   { eye: "wide",         eyeScale: 1.25, mouthCurve: -0.40, mouthOpen: 0.40, mouthScale: 0.95, browTilt:  0.35, browLift: -0.04, blush: 0.20, sweat: true  },
  scared:    { eye: "wide",         eyeScale: 1.45, mouthCurve: -0.70, mouthOpen: 0.85, mouthScale: 1.10, browTilt:  0.50, browLift: -0.06, blush: 0.05, tint: "rgba(255,140,140,0.20)", sweat: true  },
  celebrate: { eye: "starry",       eyeScale: 1.30, mouthCurve:  0.85, mouthOpen: 0.70, mouthScale: 1.30, browTilt: -0.25, browLift: -0.05, blush: 0.75, sweat: false },
};

export interface EarthFace {
  sprite: Sprite;
  setEmotion(emotion: EarthEmotion): void;
  /** Per-frame tick to drive the scale-react bounce on emotion change. */
  tick(dt: number): void;
  detach(): void;
}

function paintBackground(ctx: CanvasRenderingContext2D): void {
  // Transparent background — the face floats over the planet surface.
  ctx.clearRect(0, 0, CANVAS_SIZE, CANVAS_SIZE);
}

function paintBlush(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  alpha: number,
): void {
  if (alpha <= 0) return;
  const radius = CANVAS_SIZE * 0.055;
  ctx.save();
  ctx.fillStyle = `rgba(255, 130, 150, ${alpha.toFixed(3)})`;
  ctx.beginPath();
  ctx.ellipse(cx, cy, radius * 1.4, radius, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function paintEye(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  shape: FaceShape["eye"],
  scale: number,
): void {
  const r = CANVAS_SIZE * 0.07 * scale;
  const stroke = CANVAS_SIZE * 0.020;
  ctx.save();
  ctx.lineCap = "round";
  ctx.lineJoin = "round";

  if (shape === "closed-happy") {
    // Upward arc — content squint.
    ctx.strokeStyle = "#10131f";
    ctx.lineWidth = stroke * 1.3;
    ctx.beginPath();
    ctx.arc(cx, cy, r, Math.PI * 0.15, Math.PI * 0.85, true);
    ctx.stroke();
    ctx.restore();
    return;
  }

  if (shape === "wobble") {
    // Slightly oval, small pupil, off-center. Reads as "hesitant".
    paintEyeball(ctx, cx, cy, r, r * 0.9, stroke);
    paintPupil(ctx, cx + r * 0.18, cy + r * 0.05, r * 0.45);
    ctx.restore();
    return;
  }

  if (shape === "wide") {
    // Big circle, small pupil — alarmed.
    paintEyeball(ctx, cx, cy, r * 1.15, r * 1.15, stroke);
    paintPupil(ctx, cx, cy, r * 0.30);
    ctx.restore();
    return;
  }

  if (shape === "starry") {
    // Star-burst — hype/celebration.
    paintStarEye(ctx, cx, cy, r * 1.05);
    ctx.restore();
    return;
  }

  if (shape === "squint") {
    paintEyeball(ctx, cx, cy, r * 0.95, r * 0.55, stroke);
    paintPupil(ctx, cx, cy, r * 0.28);
    ctx.restore();
    return;
  }

  // round (default)
  paintEyeball(ctx, cx, cy, r, r, stroke);
  paintPupil(ctx, cx, cy, r * 0.55);
  ctx.restore();
}

function paintEyeball(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  rx: number,
  ry: number,
  stroke: number,
): void {
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.ellipse(cx, cy, rx, ry, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.strokeStyle = "#10131f";
  ctx.lineWidth = stroke;
  ctx.stroke();
}

function paintPupil(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.fillStyle = "#10131f";
  ctx.beginPath();
  ctx.arc(cx, cy, r, 0, Math.PI * 2);
  ctx.fill();
  // Little catchlight — the universal "alive" cue.
  ctx.fillStyle = "#ffffff";
  ctx.beginPath();
  ctx.arc(cx - r * 0.35, cy - r * 0.35, r * 0.32, 0, Math.PI * 2);
  ctx.fill();
}

function paintStarEye(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  r: number,
): void {
  ctx.fillStyle = "#ffd34d";
  ctx.strokeStyle = "#10131f";
  ctx.lineWidth = CANVAS_SIZE * 0.018;
  ctx.beginPath();
  const points = 5;
  for (let i = 0; i < points * 2; i += 1) {
    const angle = (i * Math.PI) / points - Math.PI / 2;
    const radius = i % 2 === 0 ? r : r * 0.45;
    const x = cx + Math.cos(angle) * radius;
    const y = cy + Math.sin(angle) * radius;
    if (i === 0) ctx.moveTo(x, y);
    else ctx.lineTo(x, y);
  }
  ctx.closePath();
  ctx.fill();
  ctx.stroke();
}

function paintBrow(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  tilt: number,
  side: "left" | "right",
): void {
  const len = CANVAS_SIZE * 0.11;
  const stroke = CANVAS_SIZE * 0.022;
  ctx.save();
  ctx.translate(cx, cy);
  // Mirror tilt for left vs right so they angle inward symmetrically.
  ctx.rotate(side === "left" ? tilt : -tilt);
  ctx.strokeStyle = "#10131f";
  ctx.lineCap = "round";
  ctx.lineWidth = stroke;
  ctx.beginPath();
  ctx.moveTo(-len / 2, 0);
  ctx.lineTo(len / 2, 0);
  ctx.stroke();
  ctx.restore();
}

function paintMouth(
  ctx: CanvasRenderingContext2D,
  cx: number,
  cy: number,
  curve: number,
  open: number,
  widthScale: number,
): void {
  const width = CANVAS_SIZE * 0.18 * widthScale;
  const stroke = CANVAS_SIZE * 0.024;
  const dy = -curve * width * 0.5;
  ctx.save();
  ctx.strokeStyle = "#10131f";
  ctx.lineCap = "round";
  ctx.lineJoin = "round";
  ctx.lineWidth = stroke;

  if (open > 0.05) {
    // Teardrop / open mouth — fill with dark interior + tongue dot.
    ctx.fillStyle = "#3b1a25";
    ctx.beginPath();
    ctx.moveTo(cx - width / 2, cy);
    ctx.quadraticCurveTo(cx, cy - dy * 2, cx + width / 2, cy);
    ctx.quadraticCurveTo(cx, cy + width * open * 0.55, cx - width / 2, cy);
    ctx.closePath();
    ctx.fill();
    ctx.stroke();
    // Tongue
    if (curve > 0 && open > 0.3) {
      ctx.fillStyle = "#ff7099";
      ctx.beginPath();
      ctx.ellipse(
        cx,
        cy + width * open * 0.28,
        width * 0.16,
        width * 0.08,
        0,
        0,
        Math.PI * 2,
      );
      ctx.fill();
    }
  } else {
    ctx.beginPath();
    ctx.moveTo(cx - width / 2, cy);
    ctx.quadraticCurveTo(cx, cy - dy * 2, cx + width / 2, cy);
    ctx.stroke();
  }
  ctx.restore();
}

function paintSweat(ctx: CanvasRenderingContext2D, cx: number, cy: number): void {
  ctx.save();
  ctx.fillStyle = "#7ec8ff";
  ctx.strokeStyle = "#10131f";
  ctx.lineWidth = CANVAS_SIZE * 0.014;
  ctx.beginPath();
  // Teardrop shape — classic sweat-bead.
  const w = CANVAS_SIZE * 0.030;
  const h = CANVAS_SIZE * 0.052;
  ctx.moveTo(cx, cy - h / 2);
  ctx.bezierCurveTo(cx + w, cy - h / 4, cx + w, cy + h / 2, cx, cy + h / 2);
  ctx.bezierCurveTo(cx - w, cy + h / 2, cx - w, cy - h / 4, cx, cy - h / 2);
  ctx.fill();
  ctx.stroke();
  // Highlight
  ctx.fillStyle = "rgba(255,255,255,0.7)";
  ctx.beginPath();
  ctx.ellipse(cx - w * 0.3, cy - h * 0.05, w * 0.18, h * 0.18, 0, 0, Math.PI * 2);
  ctx.fill();
  ctx.restore();
}

function paintFace(
  ctx: CanvasRenderingContext2D,
  shape: FaceShape,
): void {
  paintBackground(ctx);

  const c = CANVAS_SIZE / 2;
  const eyeOffsetX = CANVAS_SIZE * 0.18;
  const eyeY = c - CANVAS_SIZE * 0.04;
  const browY = eyeY - CANVAS_SIZE * 0.13 + shape.browLift * CANVAS_SIZE;
  const mouthY = c + CANVAS_SIZE * 0.16;

  paintBrow(ctx, c - eyeOffsetX, browY, shape.browTilt, "left");
  paintBrow(ctx, c + eyeOffsetX, browY, shape.browTilt, "right");
  paintEye(ctx, c - eyeOffsetX, eyeY, shape.eye, shape.eyeScale);
  paintEye(ctx, c + eyeOffsetX, eyeY, shape.eye, shape.eyeScale);
  paintBlush(ctx, c - eyeOffsetX * 1.55, eyeY + CANVAS_SIZE * 0.07, shape.blush);
  paintBlush(ctx, c + eyeOffsetX * 1.55, eyeY + CANVAS_SIZE * 0.07, shape.blush);
  paintMouth(ctx, c, mouthY, shape.mouthCurve, shape.mouthOpen, shape.mouthScale);
  if (shape.sweat) {
    paintSweat(ctx, c + CANVAS_SIZE * 0.30, eyeY - CANVAS_SIZE * 0.02);
  }
  if (shape.tint) {
    // Soft tint pass — red flush for scared, etc. Drawn last so it
    // washes over every feature.
    ctx.save();
    ctx.fillStyle = shape.tint;
    ctx.beginPath();
    ctx.arc(c, c, CANVAS_SIZE * 0.42, 0, Math.PI * 2);
    ctx.fill();
    ctx.restore();
  }
}

export function createEarthFace(planetRadius: number): EarthFace {
  const canvas = (typeof document !== "undefined")
    ? document.createElement("canvas")
    : null;
  // happy-dom returns null for getContext("2d") so guard before painting.
  // Browsers return a real context — the face renders normally there.
  const ctx = canvas?.getContext("2d") ?? null;
  let texture: CanvasTexture;
  if (canvas && ctx) {
    canvas.width = CANVAS_SIZE;
    canvas.height = CANVAS_SIZE;
    paintFace(ctx, SHAPES.calm);
    texture = new CanvasTexture(canvas);
    texture.minFilter = LinearFilter;
    texture.magFilter = LinearFilter;
  } else {
    // No-canvas fallback — bare stub. Face won't render in headless
    // tests, but the sprite still mounts so wiring tests pass.
    const fake =
      canvas ?? ({ width: CANVAS_SIZE, height: CANVAS_SIZE } as HTMLCanvasElement);
    texture = new CanvasTexture(fake);
  }

  const material = new SpriteMaterial({
    map: texture,
    transparent: true,
    depthTest: true,
    depthWrite: false,
    // Default invisible — the face only fades in when Earth feels a
    // crisis (nervous / worried / scared / celebrate). Calm + happy
    // states keep the face hidden so the planet starts as a serene
    // unbroken sphere.
    opacity: 0,
  });
  const sprite = new Sprite(material);
  sprite.name = "earth-face";
  // Face hugs the camera-facing front of the planet. Slightly larger
  // than the planet so the silhouette of the eyes/mouth reads strongly
  // even at the smaller play scale.
  const faceSize = planetRadius * 1.55;
  sprite.scale.set(faceSize, faceSize, 1);
  sprite.position.set(0, 0, planetRadius * 1.02);

  /** Emotions where the face fades in (visible to the player). */
  const VISIBLE_FOR: ReadonlySet<EarthEmotion> = new Set([
    "nervous",
    "worried",
    "scared",
    "celebrate",
  ]);

  let current: EarthEmotion = "calm";
  // Scale-react bounce — emotion changes pop the face briefly so the
  // player can't miss the transition.
  let reactT = 0;
  // Target opacity (0 hidden, 1 visible). lerp toward in tick.
  let targetOpacity = 0;
  let opacity = 0;

  const setEmotion = (next: EarthEmotion): void => {
    if (next === current) return;
    current = next;
    targetOpacity = VISIBLE_FOR.has(next) ? 1 : 0;
    // Only pulse the scale-react when the face is visible (or about
    // to be) — bouncing an invisible face is wasted motion.
    if (targetOpacity > 0) {
      reactT = 1;
    }
    if (ctx) {
      paintFace(ctx, SHAPES[next]);
      texture.needsUpdate = true;
    }
  };

  const tick = (dt: number): void => {
    // Smooth opacity lerp — fade in/out over ~300ms.
    const fadeRate = 4; // bigger = faster
    if (Math.abs(opacity - targetOpacity) > 0.001) {
      opacity += (targetOpacity - opacity) * Math.min(1, dt * fadeRate);
      material.opacity = opacity;
    } else if (opacity !== targetOpacity) {
      opacity = targetOpacity;
      material.opacity = opacity;
    }
    if (reactT > 0) {
      reactT = Math.max(0, reactT - dt * 4);
      // Ease-out: 1.18× peak then back to 1× over ~250ms.
      const k = reactT;
      const scale = faceSize * (1 + 0.18 * k * k);
      sprite.scale.set(scale, scale, 1);
    } else {
      sprite.scale.set(faceSize, faceSize, 1);
    }
  };

  const detach = (): void => {
    texture.dispose();
    material.dispose();
    sprite.removeFromParent();
  };

  return { sprite, setEmotion, tick, detach };
}

/**
 * F-096 — splash 등 외부에서 face 를 그릴 수 있도록 paint 헬퍼 + SHAPES 노출.
 * Three.js sprite 를 만들지 않고 임의 canvas 위에 직접 face 를 그린다.
 */
export function paintEarthFaceCanvas(
  ctx: CanvasRenderingContext2D,
  emotion: EarthEmotion,
): void {
  paintFace(ctx, SHAPES[emotion]);
}

export const FACE_CANVAS_SIZE = CANVAS_SIZE;

export const __testing = { CANVAS_SIZE, SHAPES };
